import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
} from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import config from '../config';

function ProductLocationsApp() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [filterModel, setFilterModel] = useState({
    items: [],
  });

  useEffect(() => {
    fetchData();
  }, [paginationModel, filterModel]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Handle quick filter (global search)
      const quickFilterValues = filterModel.quickFilterValues || [];
      const search = quickFilterValues.length > 0 ? quickFilterValues[0] : '';
      
      // Handle column filters - create a proper filter model structure
      // that matches what the backend expects
      const filterItems = filterModel.items || [];
      
      console.log('Original filter model:', JSON.stringify(filterModel, null, 2));
      
      // Create a properly structured filter object
      const filterObject = {
        items: filterItems,
        quickFilterValues: quickFilterValues
      };
      
      console.log('Sending filter object:', JSON.stringify(filterObject, null, 2));

      const queryParams = new URLSearchParams({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search,
        filters: JSON.stringify(filterObject)
      });

      console.log('Full query string:', queryParams.toString());

      const response = await fetch(
        `${config.apiUrl}/productlocations?${queryParams}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Response from server:', result);

      if (result.items && result.items.length > 0) {
        const firstItem = result.items[0];
        const dynamicColumns = firstItem.headers.map(header => ({
          field: header,
          headerName: header,
          flex: 1,
          minWidth: 150,
        }));
        setColumns(dynamicColumns);

        const transformedData = result.items.map((item) => ({
          id: item._id,
          ...item.data
        }));

        setData(transformedData);
        setTotalRows(result.total || 0);
      } else {
        // Handle empty results
        setData([]);
        setTotalRows(0);
        console.log('No items returned from server');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Product Locations</Typography>
        {user?.isAdmin && (
          <Tooltip title="Admin Panel">
            <IconButton onClick={() => navigate('/admin')}>
              <AdminIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <DataGrid
          rows={data}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalRows}
          paginationMode="server"
          filterMode="server"
          density="compact"
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            minHeight: 500,
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              padding: 1,
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#e3f2fd',
            }
          }}
        />
      </Paper>
    </Container>
  );
}

export default ProductLocationsApp;